import { FLAG_TYPE_LABEL } from '../schemas/flag-type';

import type { FlagAuditEntry, FlagAuditSnapshot } from '../schemas/flag';

/**
 * Turn one audit entry into the sentence the history list shows.
 *
 * Split out of `FlagHistory.tsx` because it is the only part of that component
 * with anything to get wrong. The API sends structured before/after snapshots
 * rather than pre-written text — which is what makes the log worth keeping — so
 * the prose is derived, and the derivation carries a distinction the log exists
 * for:
 *
 * `before: null` means **no row existed**, which is not the same fact as "the
 * row existed and was off". Collapsing the two would make a first write
 * indistinguishable from a toggle, and telling those apart months later is the
 * point of keeping a log at all.
 */
export function describeAuditEntry(entry: FlagAuditEntry): string {
    switch (entry.action) {
        case 'create-flag':
            return describeCreation(entry.after);
        case 'update-definition':
            return describeDefinitionChange(entry.before, entry.after);
        case 'set-default':
            return `Platform default — ${describeStateChange(entry.before, entry.after)}`;
        // Historical only: the per-environment default rows were removed by
        // NOXSCRUM-1656, but the entries written while they existed stay in the
        // log and must still read as a sentence.
        case 'set-environment-default':
            return `${environmentLabel(entry.environment)} default — ${describeStateChange(entry.before, entry.after)}`;
        case 'clear-environment-default':
            // Mirrors the `clear-override` idempotent-clear distinction below:
            // the API records "asked to clear, there was nothing there" as its
            // own fact rather than folding it into "removed the row".
            return entry.before === null
                ? `Cleared the ${environmentLabel(entry.environment)} default (there was none)`
                : `Reverted the ${environmentLabel(entry.environment)} default to the platform default`;
        case 'set-override':
            return `${entry.tenantId ?? 'A museum'} — ${describeStateChange(entry.before, entry.after)}`;
        case 'clear-override':
            // `before === null` is the idempotent clear: somebody asked to
            // revert a museum that was already on the default. The API records
            // it deliberately, so the history must not render it as if an
            // override had been removed.
            return entry.before === null
                ? `Cleared ${entry.tenantId ?? 'a museum'}'s override (there was none)`
                : `Reverted ${entry.tenantId ?? 'a museum'} to the platform default`;
    }
}

/** How the environment on a historical environment-default entry renders. */
const HISTORICAL_ENVIRONMENT_LABEL: Readonly<Record<string, string>> = {
    dev: 'Dev',
    stg: 'Staging',
    prd: 'Production',
    eph: 'Ephemeral',
};

/**
 * The entries that carry `environment` were always written with one; the
 * fallback is defensive only, so a history line never goes blank on a value it
 * merely displays.
 */
function environmentLabel(environment: FlagAuditEntry['environment']): string {
    if (environment === undefined) return 'This environment';
    return HISTORICAL_ENVIRONMENT_LABEL[environment] ?? environment;
}

function describeStateChange(
    before: FlagAuditSnapshot | null,
    after: FlagAuditSnapshot | null,
): string {
    if (after === null || after.type !== 'state') return 'cleared';

    const next = after.state;
    const previous = before?.type === 'state' ? before.state : null;

    const parts: string[] = [];

    if (previous === null) {
        parts.push(next.enabled ? 'set to on (first time)' : 'set to off (first time)');
    } else if (previous.enabled !== next.enabled) {
        parts.push(next.enabled ? 'turned on' : 'turned off');
    }

    if (previous?.rolloutPercentage !== next.rolloutPercentage) {
        parts.push(
            next.rolloutPercentage === undefined
                ? 'rollout gate removed'
                : `rollout set to ${String(next.rolloutPercentage)}%`,
        );
    }

    // Normalised: the snapshot type marks `archived` optional (override
    // snapshots never carry it), so `false` and absent are the same fact and
    // must not read as an un-archive.
    if (previous !== null && (previous.archived ?? false) !== (next.archived ?? false)) {
        parts.push(next.archived === true ? 'archived' : 'un-archived');
    }

    // A write that changed nothing observable is still a real event — somebody
    // saved this row. Saying so is more honest than rendering an empty line.
    return parts.length > 0 ? parts.join(', ') : 'saved with no change';
}

/**
 * The type is decided once, and the `create-flag` entry is the only place that
 * decision is recorded as an event — so the line says which kind was created
 * and, for `salesforce`, the field it was pointed at.
 */
function describeCreation(after: FlagAuditSnapshot | null): string {
    if (after?.type !== 'definition') return 'Created this flag';

    const { type, salesforceField } = after.definition;
    const created = `Created this ${FLAG_TYPE_LABEL[type]} flag`;

    return salesforceField !== undefined ? `${created}, targeting ${salesforceField}` : created;
}

/** How a field is named in "Edited …". The rest read fine as their own keys. */
const DEFINITION_FIELD_LABEL = {
    name: 'name',
    description: 'description',
    category: 'category',
    lifecycle: 'lifecycle',
    owner: 'owner',
    salesforceField: 'Salesforce field',
} as const;

function describeDefinitionChange(
    before: FlagAuditSnapshot | null,
    after: FlagAuditSnapshot | null,
): string {
    if (before?.type !== 'definition' || after?.type !== 'definition') return 'Edited the details';

    const changed = (
        Object.keys(DEFINITION_FIELD_LABEL) as (keyof typeof DEFINITION_FIELD_LABEL)[]
    ).filter((field) => before.definition[field] !== after.definition[field]);

    if (changed.length === 0) return 'Saved the details with no change';
    if (changed.length === 1 && changed[0] === 'lifecycle') {
        return `Lifecycle → ${after.definition.lifecycle}`;
    }
    // Named in full: the field is where the org's value lands, so which one it
    // moved to is the fact an operator reconstructing a projection needs.
    if (changed.length === 1 && changed[0] === 'salesforceField') {
        return `Salesforce field → ${after.definition.salesforceField ?? 'none'}`;
    }

    return `Edited ${changed.map((field) => DEFINITION_FIELD_LABEL[field]).join(', ')}`;
}
